import React, { useEffect, useMemo, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Filter, Container, Input, Label, Line, Tabs, Tab, TabContent, DatepickerWrapper } from "./styles";
import "react-datepicker/dist/react-datepicker.css";

const Component = ({ filter, initialFilters, addFilter, groupBy }) => {
  const [activeTab, setActiveTab] = useState("month");
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [possibleEndDate, setPossibleEndDate] = useState("");

  const initialText = useMemo(() => {
    if (initialFilters.startDate && initialFilters.endDate) {
      let parts = `01-${initialFilters.startDate}`.split("-");
      let day = parseInt(parts[0], 10);
      let month = parseInt(parts[1], 10);
      let year = parseInt(parts[2], 10);
      const newStartDate = new Date(year, month - 1, day);

      parts = `01-${initialFilters.endDate}`.split("-");
      day = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      year = parseInt(parts[2], 10);
      const newEndDate = new Date(year, month - 1, day);

      const displayText = `Desde ${format(newStartDate, "MMMM yyyy", { locale: es })} hasta ${format(newEndDate, "MMMM yyyy", { locale: es })}`;
      return displayText;
    }
    return "";
  }, [initialFilters]);

  const [value, setValue] = useState(initialText);

  const componentRef = useRef(null);

  const datePickerType = {
    month: { showMonthYearPicker: true },
    quarter: { showQuarterYearPicker: true },
    year: { showYearPicker: true },
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const cleanStartEndDate = () => {
    setStartDate("");
    setEndDate("");
    setPossibleEndDate("");
  };

  const setFilterValue = () => {
    if (startDate !== "" && endDate !== "") {
      const newStartDate = new Date(startDate);
      const newEndDate = new Date(endDate);

      if (activeTab === "month") {
        const displayText = `Desde ${format(newStartDate, "MMMM yyyy", { locale: es })} hasta ${format(newEndDate, "MMMM yyyy", { locale: es })}`;
        setValue(displayText);
        groupBy("month");
      } else if (activeTab === "quarter") {
        const displayText = `Desde Q${format(newStartDate, "Q yyyy", { locale: es })} hasta Q${format(newEndDate, "Q yyyy", { locale: es })}`;
        setValue(displayText);
        newEndDate.setMonth(newEndDate.getMonth() + 2); // Adding 2 months to the end date
        groupBy("quarter");
      } else if (activeTab === "year") {
        const displayText = `Desde ${format(newStartDate, "yyyy", { locale: es })} hasta ${format(newEndDate, "yyyy", { locale: es })}`;
        setValue(displayText);
        newStartDate.setMonth(0); // Set month to January
        newEndDate.setMonth(11); // Set month to December
        groupBy("year");
      }

      addFilter([
        { key: "startDate", value: format(newStartDate, "MM-yyyy") },
        { key: "endDate", value: format(newEndDate, "MM-yyyy") },
      ]);

      cleanStartEndDate();
    }
  };

  useEffect(() => {
    if (startDate !== "" && endDate !== "") {
      setFilterValue();
    }
  }, [startDate, endDate]);

  // This useEffect will add a click event listener when the component mounts
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (componentRef.current && !componentRef.current.contains(event.target)) {
        setCalendarVisible(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);

    // Cleanup the event listener when the component unmounts
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  return (
    <Filter className="form-group" ref={componentRef}>
      <Label htmlFor={filter.id} onClick={() => setCalendarVisible(!calendarVisible)} className="col-form-label pt-0 w-100">
        {filter.label}
        <Input value={value} type="text" placeholder="Selecciona..." readOnly />
      </Label>

      {calendarVisible && (
        <Container>
          <Tabs>
            <Tab
              type="button"
              onClick={() => {
                setActiveTab("month");
                groupBy("month");
              }}
              isActive={activeTab === "month"}
            >
              Mensual
            </Tab>
            <Tab
              type="button"
              onClick={() => {
                setActiveTab("quarter");
                groupBy("quearter");
              }}
              isActive={activeTab === "quarter"}
            >
              Trimestre
            </Tab>
            <Tab
              type="button"
              onClick={() => {
                setActiveTab("year");
                groupBy("year");
              }}
              isActive={activeTab === "year"}
            >
              Anual
            </Tab>
          </Tabs>

          <TabContent>
            <DatepickerWrapper>
              <div>Inicio: </div>
              <DatePicker
                inline
                selected={startDate}
                onChange={handleStartDateChange}
                dateFormat="MM/yyyy"
                {...datePickerType[activeTab]}
                selectsStart
                startDate={startDate}
                locale="es"
                endDate={endDate || possibleEndDate}
              />
            </DatepickerWrapper>
            <Line />
            <DatepickerWrapper>
              <div>Fin: </div>
              <DatePicker
                inline
                selected={endDate}
                onChange={(date) => {
                  handleEndDateChange(date);
                  setCalendarVisible(false);
                }}
                dateFormat="MM/yyyy"
                {...datePickerType[activeTab]}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                locale="es"
                onDayMouseEnter={(date) => startDate && !endDate && setPossibleEndDate(date)}
              />
            </DatepickerWrapper>
          </TabContent>
        </Container>
      )}
    </Filter>
  );
};

export default Component;
